import { images } from '../../images';
import { IBase, IClient, IFare, IFareData, IRouteDetails, ITown } from '../models';
import { DirectionsOrigin, ExpeditionCreationCriteria, ExpeditionType, InputType, PublishingSite, RouteVisibility, SaeAction, TicketingType, UserRole } from '../models/enums';
import { AppsService, FaresService, RouteService, SaeIntegrations, StorageService, UsersService, UtilsService } from '../services';
import { LiteralService } from '../services/literal/literal.service';
import { formData } from './routeData';

export function getNotificationsValues(route?: IRouteDetails): unknown[] {
  const notifications = [
    { id: 'email', name: 'EMAIL', code: 'EMAIL' },
    { id: 'push', name: 'PUSH', code: 'PUSH' },
    { id: 'sms', name: 'SMS*', code: 'SMS' },
  ];

  return route
    ? notifications.filter(notification => route[notification.id as keyof Object])
    : notifications;
}

export function getExpeditionAndBookingManagement(route?: IRouteDetails, literalService?: LiteralService): unknown[] {
  const functionalities = [
    { id: "dynamicEndStop", value: route?.dynamicEndStop, label: literalService!.get("dynamicEndStop", true) },
    { id: "dynamicStartStop", value: route?.dynamicStartStop, label: literalService!.get("dynamicStartStop", true) },
    { id: "goBack", value: route?.goBack, label: literalService!.get("goBack", true) },
    { id: "singleStopBookings", value: route?.singleStopBookings, label: literalService!.get("singleStopBookings", true) },
    { id: "bookOnReadyWithoutTrip", value: route?.bookOnReadyWithoutTrip, label: literalService!.get("bookOnReadyWithoutTrip", true) },
    { id: "realTimeBookings", value: route?.realTimeBookings, label: literalService!.get("realTimeBookings", true) },
    { id: "lastMinuteBookings", value: route?.lastMinuteBookings, label: literalService!.get("lastMinuteBookings", true) },
    { id: "cancelBookingsOnReady", value: route?.cancelBookingsOnReady, label: literalService!.get("cancelBookingsOnReady", true) },
    { id: "avoidCriteriasValidation", value: route?.avoidCriteriasValidation, label: literalService!.get("avoidCriteriasValidation", true) },
  ].sort((a, b) => a.label.localeCompare(b.label));

  return route
    ? functionalities.filter(functionality => route[functionality.id as keyof Object])
    : functionalities;
}

export function getVisualizationOptions(route?: IRouteDetails, literalService?: LiteralService): unknown[] {
  const functionalities = [
    { id: "notifyNewTrips", value: route?.notifyNewTrips, label: literalService!.get("notifyNewTrips", true) },
    { id: "showOnlyOptimalRoutes", value: route?.showOnlyOptimalRoutes, label: literalService!.get("showOnlyOptimalRoutes", true) },
    { id: "showUserDataToDriver", value: route?.showUserDataToDriver, label: literalService!.get("showUserDataToDriver", true) },
    { id: "showRoute", value: route?.showRoute, label: literalService!.get("showRoute", true) },
    { id: "showBusPosition", value: route?.showBusPosition, label: literalService!.get("showBusPosition", true) },
    { id: "informative", value: route?.informative, label: literalService!.get("informative", true) }
  ].sort((a, b) => a.label.localeCompare(b.label));

  return route
    ? functionalities.filter(functionality => route[functionality.id as keyof Object])
    : functionalities;
}

export async function getClientsTown(usersService: UsersService) {
  return await usersService.getPossibleClientTowns().then((res: any) => {
    const towns: any[] = [];
    const clients: any[] = [];
    const additionalClients: any[] = [];
    const fullClientsData: any[] = [];
    res.towns.map((town: ITown) => {
      towns.push({ id: town.id, value: town.id, label: town.name });
    });
    res.clients.map((client: IClient) => {
      fullClientsData.push(client);
      clients.push({ id: client.id, value: client.id, label: client.name });
      additionalClients.push({ id: client.id, name: client.name, code: client.name, maxKmsPerMonth: client.maxKmsPerMonth });
    });
    return { towns, clients, additionalClients, fullClientsData };
  });
}

export async function getApps(routeService: RouteService, routeId: number) {
  return await routeService.getApps(routeId).then((res: any) => {
    let apps: any[] = [];
    const appsList = Array.isArray(res) ? res : (res?.data?.content || []);
    appsList.forEach((app: any) => {
      apps.push(app);
    });
    return apps;
  });
}


let faresPage = 0;
let faresSearchPage = 0;
let faresItems: any[] = [];
let faresSearched: any[] = [];

async function loadFares(faresService: FaresService, clientId: number, page: number, name?: string) {
  const result = await faresService.getFares(clientId, 20, page, name);
  return result.map((fare: IFare) => ({ ...fare, value: fare.name, label: fare.name }));
}

export async function getFareValues(faresService: FaresService, clientId: number, route: IRouteDetails, fareData?: IFareData): Promise<any[]> {
  if (fareData) {
    if (fareData.showMore) {
      faresPage += 1;
      faresSearchPage += 1;
    } else {
      faresItems = [];
      faresPage = 0;
      faresSearched = [];
      faresSearchPage = 0;
    }
    if (fareData.search) {
      const results = await loadFares(faresService, clientId, faresSearchPage, fareData.value);
      faresSearched.push(...results);
    } else {
      const results = await loadFares(faresService, clientId, faresPage);
      faresItems.push(...results);
    }
  } else {
    faresItems = [];
    faresPage = 0;
    faresSearched = [];
    faresSearchPage = 0;
    const results = await loadFares(faresService, clientId, 0);
    faresItems.push(...results);
  }
  const activeFares = fareData?.search ? faresSearched : faresItems;
  const fares: any[] = [];
  fares.push({ id: null, value: '', label: '-' });
  const selectedFareId = route.fare?.id;
  if (selectedFareId) {
    const fromList = activeFares.find((f: any) => f.id === selectedFareId);
    fares.push(fromList ?? { ...route.fare, value: route.fare!.name, label: route.fare!.name });
  }
  fares.push(...activeFares.filter((f: any) => f.id !== selectedFareId));
  return fares;
}

export async function getRouteForm(route: IRouteDetails, newRoute: boolean, usersService: UsersService, literalService: LiteralService, storageService: StorageService, appsService: AppsService, utilsService: UtilsService, routeService: RouteService, saeIntegrations: SaeIntegrations, faresService: FaresService, town: ITown, managerClientName: string, edit?: boolean, fareData?: IFareData): Promise<unknown[]> {
  let towns: any[] = [];
  let clients: any[] = [];
  let additionalClients: any[] = [];
  let fullClientsData: any[] = [];
  let relateds: any[] = [];
  let saes: any[] = [];
  let expeditionTypes: any[] = [];
  let visibilities: any[] = [];
  let expeditionCreationCriterias: any[] = [];
  let ticketingTypes: any[] = [];
  let publishingSites: any[] = [];
  let publishingSitesSelected: any[] = [];
  let publishingSitesValueToChange: any[] = [];
  let saeActions: any[] = [];
  let saeActionsSelected: any[] = [];
  let saeActionsValueToChange: any[] = [];
  let directionsOrigins: any[] = [];
  let fares: any[] = [];
  const apps = route.id ? await getApps(routeService, route.id) : [];


  const res = await getClientsTown(usersService);
  towns = res.towns;
  clients = res.clients;
  fullClientsData = res.fullClientsData;
  additionalClients = res.additionalClients;

  if (edit && route.id) relateds = await routeService.getRelateds(route.id);
  
  const resSaes: any[] = await saeIntegrations.getSaes();
  saes.push({ id: null, value: '', label: '-' });
  resSaes.map((sae: IBase) => {
    saes.push({ id: sae.id, value: sae.name, label: sae.name });
  });

  const userRole = await storageService.getRole();

  const managerClientForNewRoute = fullClientsData.find((client: IClient) => client.name === managerClientName);
  if (town) route.town = town;
  // A route being created has no manager client of its own, so it takes the one resolved by name.
  // Never drop the client an existing route already carries: the whole form is built from it
  if (managerClientForNewRoute) route.managerClient = managerClientForNewRoute;

  const clientId = route.managerClient?.id;
  if (clientId) fares.push(...await getFareValues(faresService, clientId, route, fareData));

  Object.values(ExpeditionType).forEach((expeditionType: ExpeditionType) => {
    expeditionTypes.push({id: expeditionType, value: expeditionType, label: literalService.get(expeditionType, true)})
  });

  Object.values(RouteVisibility).forEach((routeVisibility: RouteVisibility) => {
    visibilities.push({id: routeVisibility, value: routeVisibility, label: literalService.get(routeVisibility, true)})
  });

  Object.values(ExpeditionCreationCriteria).forEach((expeditionCreationCriteria: ExpeditionCreationCriteria) => {
    expeditionCreationCriterias.push({id: expeditionCreationCriteria, value: expeditionCreationCriteria, label: literalService.get(expeditionCreationCriteria, true)})
  });

  Object.values(TicketingType).forEach((ticketingType: TicketingType) => {
    ticketingTypes.push({id: ticketingType, value: ticketingType, label: literalService.get(ticketingType, true)})
  });

  Object.values(PublishingSite).forEach((publishingSite: PublishingSite) => {
    publishingSites.push({id: publishingSite, value: publishingSite, label: literalService.get(publishingSite, true)});
    publishingSitesValueToChange.push(publishingSite);
  });
  publishingSitesSelected = route.publishingSites.map((publishingSite: any) => ({ id: publishingSite, value: publishingSite, label: literalService.get(publishingSite, true) }));

  Object.values(SaeAction).forEach((saeAction: SaeAction) => {
    saeActions.push({id: saeAction, value: saeAction, label: literalService.get(saeAction, true)});
    saeActionsValueToChange.push(saeAction);
  });
  saeActionsSelected = route.saeActions.map((saeAction: string) => ({ id: saeAction, value: saeAction, label: literalService.get(saeAction, true) }));

  Object.values(DirectionsOrigin).forEach((directionsOrigin: DirectionsOrigin) => {
    directionsOrigins.push({id: directionsOrigin, value: directionsOrigin, label: literalService.get(directionsOrigin, true)})
  });

  const data: formData[] = [
    {
      img: images.services.routes.expeditionType,
      title: 'services.routes.data.expeditionType',
      value: route.expeditionType || ExpeditionType[''],
      required: true,
      expanded: false,
      color: true,
      translateValue: true,
      edit: {
        id: route.expeditionType,
        inputType: InputType.SELECT_SEARCH,
        required: true,
        values: expeditionTypes,
        translateValue: true,
        valueToChange: 'expeditionType',
      },
    },
    {
      img: images.services.routes.route,
      title: 'services.routes.data.name',
      value: route.name,
      required: true,
      expanded: false,
      color: true,
      placeholder: '',
      edit: {
        inputType: InputType.TEXT,
        required: true,
        valueToChange: 'name',
      },
    },
    {
      img: images.services.routes.time,
      title: 'services.routes.data.maxTime',
      value: route.maxTime,
      required: true,
      expanded: false,
      color: true,
      placeholder: '',
      edit: {
        inputType: InputType.NUMBER,
        required: true,
        valueToChange: 'maxTime',
      },
    },
    {
      img: images.services.routes.time,
      title: 'services.routes.data.maxVariationTime',
      value: route.maxVariationTime,
      required: true,
      expanded: false,
      color: true,
      edit: {
        inputType: InputType.NUMBER,
        required: true,
        valueToChange: 'maxVariationTime',
      },
    },
    {
      img: images.services.routes.visualization,
      title: 'services.routes.data.expeditionAndBookingManagement',
      value: getExpeditionAndBookingManagement(route, literalService),
      array: true,
      required: false,
      expanded: false,
      color: true,
      edit: {
        id: getExpeditionAndBookingManagement(route, literalService),
        inputType: InputType.MULTISELECT,
        required: false,
        translateValue: true,
        emitOnChange: true,
        values: getExpeditionAndBookingManagement(undefined, literalService),
        selectedValues: getExpeditionAndBookingManagement(route, literalService),
        valueToChange: ['dynamicEndStop',
                        'dynamicStartStop',
                        'goBack',
                        'singleStopBookings',
                        'bookOnReadyWithoutTrip',
                        'realTimeBookings',
                        'lastMinuteBookings',
                        'cancelBookingsOnReady',
                        'avoidCriteriasValidation'],
      },
    },
    {
      img: images.services.routes.visibility,
      title: 'services.routes.data.visualizationOptions',
      value: getVisualizationOptions(route, literalService),
      array: true,
      required: false,
      expanded: false,
      color: true,
      edit: {
        id: getVisualizationOptions(route, literalService),
        inputType: InputType.MULTISELECT,
        required: false,
        translateValue: true,
        values: getVisualizationOptions(undefined, literalService),
        selectedValues: getVisualizationOptions(route, literalService),
        valueToChange: ['notifyNewTrips',
                        'showOnlyOptimalRoutes',
                        'showUserDataToDriver',
                        'showRoute',
                        'showBusPosition',
                        'informative'],
      },
    },
    {
      img: images.services.routes.additionalClients,
      title: 'services.routes.data.additionalClients',
      value: route.additionalClients,
      array: true,
      dataToShow: 'code',
      required: false,
      expanded: true,
      color: true,
      //hidden: userRole !== UserRole.SUPERADMIN,
      edit: {
        inputType: InputType.MULTISELECT,
        required: false,
        showSearch: true,
        values: additionalClients,
        selectedValues: route.additionalClients,
        valueToChange: 'additionalClients',
      },
    },
    {
      img: images.services.routes.visibility,
      title: 'services.routes.data.visibility',
      value: route.visibility,
      required: false,
      expanded: true,
      color: true,
      translateValue: true,
      edit: {
        id: route.visibility,
        inputType: InputType.SELECT_SEARCH,
        required: false,
        values: visibilities,
        translateValue: true,
        valueToChange: 'visibility',
      },
    },
    {
      img: images.services.routes.apps,
      title: 'services.routes.data.apps',
      value: route.apps,
      array: true,
      dataToShow: 'code',
      required: false,
      expanded: true,
      color: true,
      edit: {
        inputType: InputType.MULTISELECT,
        required: false,
        values: apps,
        selectedValues: route.apps,
        valueToChange: 'apps',
      },
    },
    {
      img: images.services.routes.ticketing,
      title: 'services.routes.data.ticketingType',
      value: route.ticketingType,
      required: false,
      expanded: true,
      color: true,
      translateValue: true,
      edit: {
        id: route.ticketingType,
        inputType: InputType.SELECT_SEARCH,
        required: false,
        values: ticketingTypes,
        translateValue: true,
        valueToChange: 'ticketingType',
      },
    },
    {
      img: images.services.routes.fare,
      title: 'services.routes.data.fare',
      value: route.fare?.name || '',
      required: !!route.ticketingType && route.ticketingType !== TicketingType.None,
      expanded: true,
      color: true,
      edit: {
        id: route.fare?.name || '',
        inputType: InputType.INFINITE_SCROLL,
        disabled: !route.ticketingType || route.ticketingType === TicketingType.None,
        required: !!route.ticketingType && route.ticketingType !== TicketingType.None,
        values: fares,
        valueToChange: 'fare',
        customSearchEnabled: true,
      },
    },
    {
      img: images.services.routes.notifications,
      title: 'services.routes.data.notificationsConfigurations',
      value: getNotificationsValues(route),
      array: true,
      required: false,
      expanded: true,
      color: true,
      edit: {
        inputType: InputType.MULTISELECT_CHECKBOXES,
        required: false,
        values: getNotificationsValues(),
        selectedValues: getNotificationsValues(route),
        extraInfo: 'services.routes.data.sms.smsAdditionalCost',
        valueToChange: ['sms', 'email', 'push'],
      },
    },
    // {
    //   img: images.services.routes.relateds,
    //   title: 'services.routes.data.relateds',
    //   value: route.relatedServices,
    //   array: true,
    //   dataToShow: 'name',
    //   required: false,
    //   expanded: true,
    //   color: true,
    //   edit: {
    //     id: route.relatedServices,
    //     inputType: InputType.MULTISELECT,
    //     required: false,
    //     values: relateds,
    //     selectedValues: route.relatedServices,
    //     valueToChange: 'relatedServices',
    //   },
    // },
    {
      img: images.services.routes.directionsOrigin,
      title: 'services.routes.data.directionsOrigin',
      value: route.directionsOrigin,
      required: false,
      expanded: true,
      color: true,
      translateValue: true,
      edit: {
        id: route.criteria,
        inputType: InputType.SELECT_SEARCH,
        required: false,
        values: directionsOrigins,
        translateValue: true,
        valueToChange: 'directionsOrigin',
      },
    },
    {
      img: images.services.routes.publishingSites,
      title: 'services.routes.data.publishingSites',
      value: route.publishingSites,
      array: true,
      dataToShow: '',
      required: false,
      expanded: true,
      color: true,
      edit: {
        inputType: InputType.MULTISELECT,
        required: false,
        values: publishingSites,
        selectedValues: publishingSitesSelected,
        valueToChange: ['publishingSites', publishingSitesValueToChange],
      },
    },
    {
      img: images.services.routes.sae,
      title: 'services.routes.data.saeIntegration',
      value: route.saeIntegration && route.saeIntegration.name || '',
      required: false,
      expanded: true,
      color: true,
      edit: {
        id: route.saeIntegration && route.saeIntegration.name || '',
        inputType: InputType.SELECT_SEARCH,
        required: false,
        values: saes,
        valueToChange: 'saeIntegration',
      }
    },
    {
      img: images.services.routes.saeActions,
      title: 'services.routes.data.saeActions',
      array: true,
      value: route.saeActions,
      required: false,
      expanded: true,
      color: true,
      translateValue: true,
      edit: {
        id: route.saeActions,
        inputType: InputType.MULTISELECT,
        required: false,
        disabled: !route.saeIntegration?.id,
        values: saeActions,
        selectedValues: saeActionsSelected,
        translateValue: true,
        valueToChange: ['saeActions', saeActionsValueToChange],
      }
    },
    {
      img: images.services.routes.criteria,
      title: 'services.routes.data.criteria',
      value: route.criteria,
      required: false,
      expanded: true,
      color: true,
      translateValue: true,
      hidden: route.expeditionType !== ExpeditionType.DynamicWithFixedPoints && route.expeditionType !== ExpeditionType.DynamicDoor2Door,
      edit: {
        id: route.criteria,
        inputType: InputType.SELECT_SEARCH,
        required: false,
        values: expeditionCreationCriterias,
        translateValue: true,
        valueToChange: 'criteria',
      },
    },
    {
      img: images.services.routes.time,
      title: 'services.routes.data.maxMergeDistance',
      value: route.maxMergeDistance || 0,
      required: false,
      expanded: true,
      color: true,
      hidden: route.expeditionType !== ExpeditionType.DynamicDoor2Door && route.expeditionType !== ExpeditionType.HybridDoor2Door,
      edit: {
        inputType: InputType.NUMBER,
        required: true,
        valueToChange: 'maxMergeDistance',
      },
    },
    {
      img: images.services.routes.time,
      title: 'services.routes.data.returnAfterEventEndMinutes',
      value: route.returnAfterEventEndMinutes ?? '',
      required: false,
      expanded: true,
      color: true,
      edit: {
        inputType: InputType.NUMBER,
        required: false,
        valueToChange: 'returnAfterEventEndMinutes',
      },
    },
  ];

  // The expedition type is the first step of the route form: until one is selected on a new
  // route, every other field stays hidden
  const expeditionTypeNotSelected = newRoute && (!route.expeditionType || route.expeditionType === ExpeditionType['']);
  if (expeditionTypeNotSelected) {
    data.forEach((element) => {
      if (element.edit?.valueToChange !== 'expeditionType') element.hidden = true;
    });
  }
  return data;
}